import React, { useEffect, useState, lazy } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { Button } from 'reactstrap';
import { connect } from 'react-redux';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';

import RateAndRecommend from 'Components/ProductReviews/UserReviews/RateAndRecommend';
import ReviewTextForm from 'Components/ProductReviews/UserReviews/ReviewTextForm';
import ReviewStatusBar from 'Components/ProductReviews/UserReviews/ReviewStatusBar';
import { getReviewItemByProductId } from 'Redux/ducks/user-review';
import OrderItemSummary from 'Components/ProductReviews/OrderItemReviews/OrderItemSummary';
import ReviewGuidelines from 'Components/ProductReviews/OrderItemReviews/ReviewGuidelines';
import { history } from 'Redux/store';
import { toggleLoginModal } from 'Redux/ducks/modal';
import LazyModal from 'Components/Utility/LazyModal';
import 'Components/ProductReviews/UserReviews/ReviewTextForm.scss';
import 'Components/ProductReviews/OrderItemReviews/OrderItemReviewForm.scss';
import getUrlParameter from 'Helpers/Url/GetUrlParameter';

const DeleteReviewModal = lazy(() => import(/* webpackChunkName: "DeleteReviewModal" */ 'Components/ProductReviews/Modals/DeleteReviewModal'));


const OrderItemReviewForm = ({
  match,
  authenticated,
  toggleLoginModal: _toggleLoginModal,
}) => {
  const [reviewItem, setReviewItem] = useState({});
  const [hasLoaded, setHasLoaded] = useState(false);
  const [canSubmit, setCanSubmit] = useState(false);
  const [reason, setReason] = useState('');
  const [deleteModalopen, setDeleteModalOpen] = useState(false);
  const { params: { productId } } = match;

  let context = 'Account area';
  const utmSource = getUrlParameter('utm_source');

  if (utmSource) context = 'Account area from email reminder';

  useEffect(() => {
    const loadFormData = async () => {
      const { reviewItem: _reviewItem, canSubmit: _canSubmit, reason: _reason } =
      await getReviewItemByProductId(productId);

      if (_reviewItem) setReviewItem(_reviewItem);

      setCanSubmit(_canSubmit);
      setReason(_reason);

      setHasLoaded(true);
    };

    if (authenticated) loadFormData();
  }, [productId, authenticated]);

  const onSubmitSuccess = (published) => {
    if (published) history.push(`/reviews?reviewStatus=published&reviewPid=${reviewItem.product_id}`);

    if (!published) history.push(`/reviews?reviewStatus=submitted&reviewPid=${reviewItem.product_id}`);
  };

  const topSection = () =>
    <div className="account-content top-container">
      <h1>
        {reviewItem.has_active_version && <FormattedMessage id="orderitemreviewform.editproductreview" defaultMessage="Edit Product Review" />}
        {!reviewItem.has_active_version && <FormattedMessage id="orderitemreviewform.writeaproductreview" defaultMessage="Write a Product Review" />}
      </h1>
    </div>;


  if (!authenticated) {
    return (
      <div className="order-item-review-form">
        {topSection()}
        <div className="login-button-container">
          <Button className="login-to-review-button" color="link" onClick={_toggleLoginModal}>
            <FormattedMessage id="orderitemreviewform.logintowrite" defaultMessage="Login to write a product review" />
          </Button>
        </div>
      </div>
    );
  }

  const order = get(reviewItem, 'order');

  if (!hasLoaded) return false;

  return (
    <div className="order-item-review-form">
      {topSection()}

      {reviewItem.has_active_version &&
        <ReviewStatusBar reviewData={reviewItem} />
      }

      <div className="order-item-review-form-content">
        <div className="summary-guidelines-container">
          <div className="review-item-column">
            {
              !isEmpty(reviewItem) &&
              <OrderItemSummary item={reviewItem} order={order} />
            }
          </div>
          <div className="guidelines-column">
            <ReviewGuidelines />
          </div>
        </div>


        {hasLoaded && !canSubmit && reason !== 'user reviews disabled on this product' &&
          <div className="not-revealed-key-container">
            <FormattedMessage id="orderitemreviewform.cantsubmit" defaultMessage="This product is not eligible for review" />
          </div>
        }
        {hasLoaded && !canSubmit && reason === 'user reviews disabled on this product' &&
          <div className="not-revealed-key-container">
            <FormattedMessage id="orderitemreviewform.wearenotaccepting" defaultMessage="We are not accepting reviews for this product at this time. Please check back soon" />
          </div>
        }

        {canSubmit &&
          <React.Fragment>
            <div className="rating-recommend-container">
              <RateAndRecommend
                slug={reviewItem.slug}
                reviewData={reviewItem}
                productId={productId}
                updateParentReviewData={setReviewItem}
              />
            </div>

            <div className="review-text-form-container">
              <ReviewTextForm
                slug={reviewItem.slug}
                productId={productId}
                reviewData={reviewItem}
                updateParentReviewData={setReviewItem}
                context={context}
                successCallBack={onSubmitSuccess}
              />
            </div>

            {reviewItem.has_active_version &&
              <div className="delete-button-container">
                <Button
                  color="link"
                  onClick={() => setDeleteModalOpen(!deleteModalopen)}
                >
                  <FormattedMessage id="orderitemreviewform.deletereview" defaultMessage="Delete review" />
                </Button>

                <LazyModal
                  centered
                  open={deleteModalopen}
                  setOpen={setDeleteModalOpen}
                  className="simple-modal"
                >
                  <DeleteReviewModal reviewId={reviewItem._id} setOpen={setDeleteModalOpen} />
                </LazyModal>
              </div>
            }

          </React.Fragment>
        }
      </div>
    </div>
  );
};

OrderItemReviewForm.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({
      iid: PropTypes.string,
    }),
  }).isRequired,
  authenticated: PropTypes.bool,
  toggleLoginModal: PropTypes.func.isRequired,
};

OrderItemReviewForm.defaultProps = {
  authenticated: false,
};


const mapStateToProps = ({
  auth: { authenticated },
}) => ({
  authenticated,
});

const mapDispatchToProps = {
  toggleLoginModal,
};

export default connect(mapStateToProps, mapDispatchToProps)(OrderItemReviewForm);
